import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Button, Input, Select, Row, Col, Checkbox, DatePicker } from 'antd';
import { useHistory } from 'react-router';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { EditOutlined } from '@ant-design/icons';
import Modal from 'antd/lib/modal/Modal';
import moment from 'moment';

import GetField from '../Components/GetField';
import styles from '../styles';
import { successNotification, errorNotification } from '../Snackbar';
import Api from '../Api';
import LeftGrid from '../Components/LeftGrid';
import RightGrid from '../Components/RightGrid';
import {
  getTimeActionToCreate,
  clearTimeAction,
  getUsers,
  clearUsers,
} from '../Actions';
import Breadcrumbs from '../Components/Breadcrumbs';
import timeactions from '../timeactions';

const AntInput = GetField(Input);
const AntDatePicker = GetField(DatePicker);
const AntSelect = GetField(Select);
const AntCheckbox = GetField(Checkbox);
const { Option } = Select;
const AntInputTextArea = GetField(Input.TextArea);

const CreateTimeAction = props => {
  const { classes, match } = props;

  const history = useHistory();
  const dispatch = useDispatch();

  const timeAction = useSelector(state => state.timeAction);
  const users = useSelector(state => state.users);

  const [ItemModal, SetItemModal] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const { handleSubmit, control, reset, getValues, setValue } = useForm({
    shouldUnregister: false,
  });

  const { fields } = useFieldArray({
    control,
    name: 'activities',
  });

  useEffect(() => {
    dispatch(getTimeActionToCreate(match.params.jobchartId));
    dispatch(getUsers());
    return () => {
      dispatch(clearTimeAction());
      dispatch(clearUsers());
    };
  }, []);

  useEffect(() => {
    reset(timeAction);
  }, [timeAction]);

  const hideModal = () => {
    SetItemModal(null);
  };

  const disabledDate = useCallback(
    time => time.toDate() < moment().startOf('day').toDate(),
    []
  );

  const onSubmit = useCallback(async formValues => {
    try {
      setConfirmLoading(true);
      await Api.post('/timeactions', formValues);
      successNotification('Time & Action Plan added.');
      history.push('/jobcharts/list');
    } catch (err) {
      setConfirmLoading(false);
      errorNotification(err);
    }
  }, []);

  const setPlanDate = (itemIndex, subActivityIndex, currentvalue) => {
    const activity = getValues(`activities[${itemIndex}]`);

    if (activity) {
      const { subActivities } = activity;

      const maxedDate = subActivities
        .filter((el, index) => el.plannedAt && index !== subActivityIndex)
        .reduce(
          (acc, el) =>
            acc > el.plannedAt.toDate() ? acc : el.plannedAt.toDate(),
          currentvalue
        );

      setValue(
        `activities[${itemIndex}].plannedAt`,
        maxedDate ? moment(maxedDate) : null
      );
    }
  };

  return (
    <>
      <Breadcrumbs
        moduleName="Jobcharts"
        moduleLink="/jobcharts/list"
        subModuleName="Time & Action Create"
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <LeftGrid>
            <AntInput
              label="Customer"
              placeholder="Customer"
              value={timeAction.customer}
              disabled
            />
          </LeftGrid>
          <RightGrid>
            <AntInput
              label="BPO #"
              placeholder="BPO #"
              value={timeAction.bpoNo}
              disabled
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="intPPMD"
              control={control}
              render={({ field }) => (
                <AntDatePicker
                  allowClear
                  label="Int. PPMD"
                  placeholder="Int. PPMD"
                  disabledDate={disabledDate}
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="extPPMD"
              control={control}
              render={({ field }) => (
                <AntDatePicker
                  allowClear
                  label="Ext. PPMD"
                  placeholder="Ext. PPMD"
                  disabledDate={disabledDate}
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="isReorder"
              control={control}
              render={({ field }) => (
                <AntCheckbox
                  size="large"
                  allowClear
                  label="Is Reorder?"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="remark"
              control={control}
              render={({ field }) => (
                <AntInputTextArea
                  allowClear
                  label="Remark"
                  placeholder="Remark"
                  rows={1}
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <strong>Products: </strong>
            {timeAction.products}
          </LeftGrid>
        </Row>
        <br />
        <Row>
          <Col
            xs={{ span: 21, offset: 1 }}
            md={{ span: 21, offset: 1 }}
            lg={{ span: 20, offset: 2 }}
          >
            <table className={classes.inputTable}>
              <thead className={classes.inputThead}>
                <tr>
                  <th className={classes.inputTh}>Task</th>
                  <th className={classes.inputTh} style={{ width: '30%' }}>
                    Assignee
                  </th>
                  <th className={classes.inputTh} style={{ width: '30%' }}>
                    Plan Date
                  </th>
                  <th className={classes.inputTh} style={{ width: '100px' }}>
                    Sub-Tasks
                  </th>
                </tr>
              </thead>
              <tbody>
                {fields.map((item, itemIndex) => (
                  <tr key={item.id}>
                    <td className={classes.inputTd}>{item.task}</td>
                    <td className={classes.inputTd}>
                      <Controller
                        name={`activities[${itemIndex}].assigneeId`}
                        control={control}
                        render={({ field }) => (
                          <AntSelect
                            allowClear
                            placeholder="Select Assignee"
                            {...field}
                          >
                            {users.map((user, userIndex) => (
                              <Option key={userIndex} value={user.id}>
                                {user.name}
                              </Option>
                            ))}
                          </AntSelect>
                        )}
                      />
                    </td>
                    <td className={classes.inputTd}>
                      <Controller
                        name={`activities[${itemIndex}].plannedAt`}
                        control={control}
                        render={({ field }) => (
                          <AntDatePicker
                            allowClear
                            placeholder="Plan Date"
                            disabledDate={disabledDate}
                            disabled={
                              item.subActivities && item.subActivities.length
                            }
                            {...field}
                          />
                        )}
                      />
                    </td>
                    <td className={classes.inputTd}>
                      {item.subActivities && item.subActivities.length ? (
                        <div style={{ textAlign: 'center', width: '100%' }}>
                          <EditOutlined
                            onClick={() => {
                              SetItemModal({
                                task: item.task,
                                subActivities: item.subActivities,
                                index: itemIndex,
                              });
                            }}
                          />
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Col>
        </Row>
        <br />
        <Row className={classes.buttonContainer}>
          <Button type="primary" loading={confirmLoading} htmlType="submit">
            Submit
          </Button>
        </Row>
        <br />
        {ItemModal &&
        [
          timeactions.greigeFabricOrderDate,
          timeactions.greigeFabricInhouseDate,
        ].includes(ItemModal.task) ? (
          <Modal
            title={ItemModal.task}
            visible
            centered
            maskClosable={false}
            closable={false}
            width="80%"
            onOk={hideModal}
            okText="Close"
            cancelButtonProps={{ style: { display: 'none' } }}
          >
            <Row>
              <table className={classes.inputTable}>
                <thead className={classes.inputThead}>
                  <tr>
                    <th className={classes.inputTh}>Product</th>
                    <th className={classes.inputTh}>Fabric</th>
                    <th className={classes.inputTh}>Width</th>
                    <th className={classes.inputTh}>Assignee</th>
                    <th className={classes.inputTh}>PlannedAt</th>
                  </tr>
                </thead>
                <tbody>
                  {ItemModal
                    ? ItemModal.subActivities.map((subActivity, index) => (
                        <tr key={index}>
                          <td className={classes.inputTd}>
                            {subActivity.product}
                          </td>
                          <td className={classes.inputTd}>
                            {subActivity.fabric}
                          </td>
                          <td className={classes.inputTd}>
                            {subActivity.width}
                          </td>
                          <td className={classes.inputTd}>
                            <Controller
                              name={`activities[${ItemModal.index}].subActivities[${index}].assigneeId`}
                              control={control}
                              render={({ field }) => (
                                <AntSelect
                                  allowClear
                                  placeholder="Select Assignee"
                                  {...field}
                                >
                                  {users.map((user, userIndex) => (
                                    <Option key={userIndex} value={user.id}>
                                      {user.name}
                                    </Option>
                                  ))}
                                </AntSelect>
                              )}
                            />
                          </td>
                          <td className={classes.inputTd}>
                            <Controller
                              name={`activities[${ItemModal.index}].subActivities[${index}].plannedAt`}
                              control={control}
                              render={({ field }) => (
                                <AntDatePicker
                                  allowClear
                                  placeholder="Plan Date"
                                  disabledDate={disabledDate}
                                  {...field}
                                  onChange={value => {
                                    setPlanDate(
                                      ItemModal.index,
                                      index,
                                      value ? value.toDate() : null
                                    );
                                    field.onChange(value);
                                  }}
                                />
                              )}
                            />
                          </td>
                        </tr>
                      ))
                    : null}
                </tbody>
              </table>
            </Row>
          </Modal>
        ) : null}
        {ItemModal &&
        [
          timeactions.dyedFabricInhouseDate,
          timeactions.printedFabricInhouseDate,
          timeactions.acidWashFabricInhouseDate,
          timeactions.bulkLotSendApprovalDate,
        ].includes(ItemModal.task) ? (
          <Modal
            title={ItemModal.task}
            visible
            centered
            maskClosable={false}
            closable={false}
            width="80%"
            onOk={hideModal}
            okText="Close"
            cancelButtonProps={{ style: { display: 'none' } }}
          >
            <Row>
              <table className={classes.inputTable}>
                <thead className={classes.inputThead}>
                  <tr>
                    <th className={classes.inputTh}>Product</th>
                    <th className={classes.inputTh}>Fabric</th>
                    <th className={classes.inputTh}>Width</th>
                    <th className={classes.inputTh}>Base Color</th>
                    <th className={classes.inputTh}>Print Color</th>
                    <th className={classes.inputTh}>Assignee</th>
                    <th className={classes.inputTh}>PlannedAt</th>
                  </tr>
                </thead>
                <tbody>
                  {ItemModal
                    ? ItemModal.subActivities.map((subActivity, index) => (
                        <tr key={index}>
                          <td className={classes.inputTd}>
                            {subActivity.product}
                          </td>
                          <td className={classes.inputTd}>
                            {subActivity.fabric}
                          </td>
                          <td className={classes.inputTd}>
                            {subActivity.width}
                          </td>
                          <td className={classes.inputTd}>
                            {subActivity.baseColor}
                          </td>
                          <td className={classes.inputTd}>
                            {subActivity.printColor}
                          </td>
                          <td className={classes.inputTd}>
                            <Controller
                              name={`activities[${ItemModal.index}].subActivities[${index}].assigneeId`}
                              control={control}
                              render={({ field }) => (
                                <AntSelect
                                  allowClear
                                  placeholder="Select Assignee"
                                  {...field}
                                >
                                  {users.map((user, userIndex) => (
                                    <Option key={userIndex} value={user.id}>
                                      {user.name}
                                    </Option>
                                  ))}
                                </AntSelect>
                              )}
                            />
                          </td>
                          <td className={classes.inputTd}>
                            <Controller
                              name={`activities[${ItemModal.index}].subActivities[${index}].plannedAt`}
                              control={control}
                              render={({ field }) => (
                                <AntDatePicker
                                  allowClear
                                  placeholder="Plan Date"
                                  disabledDate={disabledDate}
                                  {...field}
                                  onChange={value => {
                                    setPlanDate(
                                      ItemModal.index,
                                      index,
                                      value ? value.toDate() : null
                                    );
                                    field.onChange(value);
                                  }}
                                />
                              )}
                            />
                          </td>
                        </tr>
                      ))
                    : null}
                </tbody>
              </table>
            </Row>
          </Modal>
        ) : null}
        {ItemModal &&
        [timeactions.AccOrderInhouseDate].includes(ItemModal.task) ? (
          <Modal
            title={ItemModal.task}
            visible
            centered
            maskClosable={false}
            closable={false}
            width="80%"
            onOk={hideModal}
            okText="Close"
            cancelButtonProps={{ style: { display: 'none' } }}
          >
            <Row>
              <table className={classes.inputTable}>
                <thead className={classes.inputThead}>
                  <tr>
                    <th className={classes.inputTh}>Product</th>
                    <th className={classes.inputTh}>Accessory</th>
                    <th className={classes.inputTh}>Assignee</th>
                    <th className={classes.inputTh}>PlannedAt</th>
                  </tr>
                </thead>
                <tbody>
                  {ItemModal
                    ? ItemModal.subActivities.map((subActivity, index) => (
                        <tr key={index}>
                          <td className={classes.inputTd}>
                            {subActivity.product}
                          </td>
                          <td className={classes.inputTd}>
                            {subActivity.accessory}
                          </td>
                          <td className={classes.inputTd}>
                            <Controller
                              name={`activities[${ItemModal.index}].subActivities[${index}].assigneeId`}
                              control={control}
                              render={({ field }) => (
                                <AntSelect
                                  allowClear
                                  placeholder="Select Assignee"
                                  {...field}
                                >
                                  {users.map((user, userIndex) => (
                                    <Option key={userIndex} value={user.id}>
                                      {user.name}
                                    </Option>
                                  ))}
                                </AntSelect>
                              )}
                            />
                          </td>
                          <td className={classes.inputTd}>
                            <Controller
                              name={`activities[${ItemModal.index}].subActivities[${index}].plannedAt`}
                              control={control}
                              render={({ field }) => (
                                <AntDatePicker
                                  allowClear
                                  placeholder="Plan Date"
                                  disabledDate={disabledDate}
                                  {...field}
                                  onChange={value => {
                                    setPlanDate(
                                      ItemModal.index,
                                      index,
                                      value ? value.toDate() : null
                                    );
                                    field.onChange(value);
                                  }}
                                />
                              )}
                            />
                          </td>
                        </tr>
                      ))
                    : null}
                </tbody>
              </table>
            </Row>
          </Modal>
        ) : null}
      </form>
    </>
  );
};

CreateTimeAction.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default withStyles(styles)(CreateTimeAction);
